import {
  ApprovalForm,
  type ApprovalFormFields,
} from "@/components/ApprovalForm/ApprovalForm";
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from "@/components/ui/buttons/PrimaryButton";
import {
  type FlowSkipType,
  type PurchaseRequestType,
  PurchaseRequestTypeMap,
} from "@/constants";
import { useModal } from "@/hooks/modals/useModal";
import { useFlow } from "@/hooks/services/useFlow";
import {
  notifyError,
  notifySuccess,
  notifyWarning,
} from "@/utils/notification";
import { FormInstance, Space } from "antd";

import {
  SrmPurchaseRequestDetailVo,
  SrmPurchaseRequestDto,
} from "@/types/apis";
import {
  HighValuePurchaseRequisitionFormValue,
  LowValuePurchaseRequisitionFormValue,
  PurchaseRequestForm,
  RdRawMaterialRequisitionFormValue,
  RldReferenceProductRequisitionFormValue,
} from "../type";
import { useFlowNodeMatcher } from "./useFlowNodeMatcher";
import {
  usePurchaseRequestDetail,
  useSaveAndApplyPurchaseRequest,
} from "./usePurchaseRequest";
import { convertFileItemsToString } from "@/utils/file";
import { resolveSkipType } from "@/utils/flow.helper";
import {
  makeVariableForHighValueFinancialFirstAudit,
  makeVariableForLowValueFinancialFirstAudit,
  makeHighValuePurchaseRequisition,
  makeLowValuePurchaseRequisition,
  makeRdRawMaterialRequisition,
  makeRldReferenceProductRequisition,
  makeVariableForHighValuePurchasingStaffApprove,
  makeVariableForLowValuePurchasingStaffApprove,
  makeVariableForNormalFinancialFirstAudit,
} from "./lib/makeParams";
import { extractFormFieldError } from "@/utils/form.helper";
import { useTranslation } from "react-i18next";

type ApprovalStrategy = (form: FormInstance) => Promise<Record<string, any>>;

type FooterKey =
  | "edit"
  | "close"
  | "approve"
  | "orderConfirm"
  | "purchaseManagerApproveForRldOrRd"
  | "purchasingStaffApproveForHighOrLow"
  | "purchasingManagerApproveForLow"
  | "applicantAddOrUpdateFilesApproveForHigh"
  | "purchaserAddOrUpdateFilesApproveForHigh"
  | "financialFirstAuditApprove"
  | "uploadContractSubmit"
  | "createFixedAssetsNoSubmit";

function resolveFooterKey(ctx: {
  isStartedMode: boolean;
  isInReviewMode: boolean;
  viewStatus: boolean;
  isReadOnly: boolean;
  reviewStatus?: string;
  matcher: ReturnType<typeof useFlowNodeMatcher>;
  type?: PurchaseRequestType;
}): FooterKey {
  const {
    isStartedMode,
    isInReviewMode,
    viewStatus,
    isReadOnly,
    reviewStatus,
    matcher,
    type,
  } = ctx;

  /**
   * 先处理不可操作状态
   * 再处理流程节点匹配
   * 最后兜底编辑状态
   */

  if (
    (isStartedMode && reviewStatus === "2") ||
    (isStartedMode && reviewStatus === "3" && isReadOnly) ||
    (isStartedMode && reviewStatus === "4") ||
    (isInReviewMode && viewStatus === false)
  ) {
    return "close";
  }

  if (isInReviewMode && viewStatus) {
    if (
      matcher.isMatchPurchaseManagerAuditNodeForRLD ||
      matcher.isMatchPurchaseManagerAuditNodeForRD
    ) {
      return "purchaseManagerApproveForRldOrRd";
    }
    if (matcher.isMatchPurchasingStaffFillInNodeForHighOrLow)
      return "purchasingStaffApproveForHighOrLow";
    if (matcher.isMatchApplicantAddOrUpdateFilesNodeForHigh)
      return "applicantAddOrUpdateFilesApproveForHigh";
    if (matcher.isMatchPurchaserAddOrUpdateFilesNodeForHigh)
      return "purchaserAddOrUpdateFilesApproveForHigh";
    if (matcher.isMatchCreateFixedAssetsNoNode)
      return "createFixedAssetsNoSubmit";
    if (matcher.isMatchFinancialFirstAuditNode)
      return "financialFirstAuditApprove";
    if (matcher.isMatchUploadContractNode) return "uploadContractSubmit";
    if (matcher.isMatchFillInPONode) return "orderConfirm";
  }

  if (
    matcher.isMatchPurchaseManagerAuditNodeForLow &&
    type === PurchaseRequestTypeMap.LowValuePurchaseRequisition
  )
    return "purchasingManagerApproveForLow";

  if (isInReviewMode && reviewStatus === "2" && viewStatus) {
    return "approve";
  }

  return "edit";
}

export function useFormFooter(
  form: FormInstance<PurchaseRequestForm>,
  cleanup: () => void,
) {
  const { props } = useModal("PURCHASE_REQUEST");
  const { recordId, mode, viewStatus, isReadOnly } = props;

  const detail = usePurchaseRequestDetail(recordId);
  const matcher = useFlowNodeMatcher(detail?.insId);
  const operations = useOperation({
    form,
    cleanup,
    detail,
  });

  const { t } = useTranslation("purchaseRequestManagement");
  const { t: tCommon } = useTranslation("common");

  const footerMap: Record<FooterKey, React.ReactNode> = {
    edit: (
      <Space>
        <PrimaryOutlineButton onClick={operations.saveDraft}>
          {t("暂存")}
        </PrimaryOutlineButton>
        <PrimaryButton onClick={operations.submit}>{t("提交")}</PrimaryButton>
      </Space>
    ),
    close: (
      <Space>
        <PrimaryOutlineButton onClick={cleanup}>
          {tCommon("关闭")}
        </PrimaryOutlineButton>
      </Space>
    ),
    approve: <ApprovalForm onApprovalSubmit={operations.approve} />,
    orderConfirm: (
      <PrimaryButton onClick={operations.orderConfirm}>
        {t("提交")}
      </PrimaryButton>
    ),
    purchaseManagerApproveForRldOrRd: (
      <PrimaryButton onClick={operations.purchaseManagerApproveForRldOrRd}>
        {t("提交")}
      </PrimaryButton>
    ),
    purchasingStaffApproveForHighOrLow: (
      <ApprovalForm
        onApprovalSubmit={operations.purchasingStaffApproveForHighOrLow}
      />
    ),
    purchasingManagerApproveForLow: (
      <ApprovalForm
        onApprovalSubmit={operations.purchasingManagerApproveForLow}
      />
    ),
    applicantAddOrUpdateFilesApproveForHigh: (
      <ApprovalForm
        onApprovalSubmit={operations.applicantAddOrUpdateFilesApproveForHigh}
      />
    ),
    purchaserAddOrUpdateFilesApproveForHigh: (
      <ApprovalForm
        onApprovalSubmit={operations.purchaserAddOrUpdateFilesApproveForHigh}
      />
    ),
    financialFirstAuditApprove: (
      <ApprovalForm onApprovalSubmit={operations.financialFirstAuditApprove} />
    ),
    uploadContractSubmit: (
      <ApprovalForm onApprovalSubmit={operations.uploadContractSubmit} />
    ),
    createFixedAssetsNoSubmit: (
      <ApprovalForm onApprovalSubmit={operations.createFixedAssetsNoSubmit} />
    ),
  };

  return () => {
    const key = resolveFooterKey({
      isStartedMode: mode === "started",
      isInReviewMode: mode === "inReview",
      viewStatus: viewStatus ?? false,
      isReadOnly: isReadOnly ?? false,
      reviewStatus: detail?.reviewStatus?.toString(),
      matcher,
      type: detail?.formType as PurchaseRequestType,
    });
    return footerMap[key];
  };
}

type SubmitParamMap = {
  [PurchaseRequestTypeMap.RdRawMaterialRequisition]: SrmPurchaseRequestDto;
  [PurchaseRequestTypeMap.RldReferenceProductRequisition]: SrmPurchaseRequestDto;
  [PurchaseRequestTypeMap.HighValuePurchaseRequisition]: SrmPurchaseRequestDto;
  [PurchaseRequestTypeMap.LowValuePurchaseRequisition]: SrmPurchaseRequestDto;
};

// 组装提交参数
const makeSubmitParams = (
  values: PurchaseRequestForm,
  type: PurchaseRequestType,
  isDraft: boolean,
): SubmitParamMap[PurchaseRequestType] => {
  switch (type) {
    case PurchaseRequestTypeMap.RdRawMaterialRequisition:
      return makeRdRawMaterialRequisition(
        values as RdRawMaterialRequisitionFormValue,
      );
    case PurchaseRequestTypeMap.RldReferenceProductRequisition:
      return makeRldReferenceProductRequisition(
        values as RldReferenceProductRequisitionFormValue,
      );
    case PurchaseRequestTypeMap.HighValuePurchaseRequisition:
      return makeHighValuePurchaseRequisition(
        values as HighValuePurchaseRequisitionFormValue,
      );
    case PurchaseRequestTypeMap.LowValuePurchaseRequisition:
      return makeLowValuePurchaseRequisition(
        values as LowValuePurchaseRequisitionFormValue,
        isDraft,
      );
    default:
      return makeLowValuePurchaseRequisition(
        values as LowValuePurchaseRequisitionFormValue,
        isDraft,
      );
  }
};

function useFlowExecutor({
  taskId,
  cleanup,
  onSuccess,
}: {
  taskId?: number;
  cleanup: () => void;
  onSuccess?: () => void;
}) {
  const { t } = useTranslation("purchaseRequestManagement");
  const { passFlowMutation } = useFlow("PURCHASE_REQUEST", false);

  return (params: {
    skipType: FlowSkipType;
    message: string;
    variable: Record<string, any>;
  }) => {
    if (!taskId) return;

    passFlowMutation.mutate(
      {
        taskId,
        flowParamsDto: {
          flowCode: "PURCHASE_REQUEST",
          skipType: params.skipType,
          message: params.message,
          variable: params.variable,
        },
      },
      {
        onSuccess() {
          cleanup();
          notifySuccess(t("提交成功"));
          onSuccess?.();
        },
      },
    );
  };
}

function useOperation({
  form,
  cleanup,
  detail,
}: {
  detail?: SrmPurchaseRequestDetailVo;
  form: FormInstance<PurchaseRequestForm>;
  cleanup: () => void;
}) {
  const { t } = useTranslation("purchaseRequestManagement");
  const { props } = useModal("PURCHASE_REQUEST");

  const { recordId, onSuccess, taskId, type } = props;

  // 保存并申请
  const submitMutation = useSaveAndApplyPurchaseRequest({
    onSuccess() {
      onSuccess?.();
      notifySuccess(t("提交成功"));
      cleanup();
    },
  });

  // 暂存
  const saveDraft = () => {
    const values = form.getFieldsValue();

    const params = makeSubmitParams(values, type, true);

    submitMutation.mutate({
      ...params,
      id: recordId,
      reviewStatus: 1,
    });
  };

  // 提交
  const submit = async () => {
    try {
      const values = await form.validateFields();

      const params = makeSubmitParams(values, type, false);

      if (Object.keys(params).length === 0) return;

      submitMutation.mutate({
        ...params,
        id: recordId,
        reviewStatus: 2,
      });
    } catch (error) {
      notifyWarning(t("请检查表单填写是否完整"));
    }
  };

  const executeApprove = useFlowExecutor({
    taskId,
    cleanup,
    onSuccess,
  });

  // 正常审批
  const approve = async (values: ApprovalFormFields) => {
    executeApprove({
      skipType: values.skipType === "REJECT" ? "" : "PASS", // 驳回时传空处理
      message: values.message,
      variable: {},
    });
  };

  // RLD/RD 采购经理审批
  const purchaseManagerApproveForRldOrRd = async () => {
    if (
      detail?.formType !==
        PurchaseRequestTypeMap.RldReferenceProductRequisition &&
      detail?.formType !== PurchaseRequestTypeMap.RdRawMaterialRequisition
    )
      return;

    let variable: Record<string, any>;

    try {
      const values = await (
        form as FormInstance<
          | RldReferenceProductRequisitionFormValue
          | RdRawMaterialRequisitionFormValue
        >
      ).validateFields();
      variable = {
        supplierList: [
          {
            supplierId: values.supplierId,
            supplierCode: values.supplierCode,
            supplierName: values.supplierName,
            quantity: values.quantity,
            quantityUnit: values.quantityUnit,
            price: values.price,
            priceUnit: values.priceUnit,
            priceHkd: values.priceHkd,
            totalAmount: values.totalAmount,
            totalAmountUnit: values.totalAmountUnit,
            totalAmountHkd: values.totalAmountHkd,
            remarks: values.remarks,
            quotationFiles: convertFileItemsToString(values.quotationFiles),
          },
        ],
      };
    } catch (error) {
      return notifyWarning(t("请检查表单填写是否完整"));
    }
    executeApprove({
      skipType: "PASS",
      message: t("审批通过"),
      variable,
    });
  };

  // 订单确认 （填写PO）
  const orderConfirm = async () => {
    const hasPrNo = Boolean(detail?.prNo);
    if (!hasPrNo) {
      return notifyWarning(t("请先生成PR编号"));
    }

    let variable: Record<string, any>;
    try {
      const values = await form.validateFields();
      variable = {
        prNo: values.prNo,
        poNo: values.poNo,
      };
    } catch (error) {
      return notifyWarning(extractFormFieldError(error));
    }
    executeApprove({
      skipType: "PASS",
      message: t("订单确认"),
      variable,
    });
  };

  // 采购人员审核 （High Value/Low Value) 策略注册
  const MAKE_VARIABLE_FOR_PURCHASING_STAFF_APPROVE_STRATEGIES: Record<
    string,
    ApprovalStrategy
  > = {
    [PurchaseRequestTypeMap.HighValuePurchaseRequisition]:
      makeVariableForHighValuePurchasingStaffApprove,
    [PurchaseRequestTypeMap.LowValuePurchaseRequisition]:
      makeVariableForLowValuePurchasingStaffApprove,
  };

  // 采购审批 （High Value/Low Value)
  const purchasingStaffApproveForHighOrLow = async (
    approveValues: ApprovalFormFields,
  ) => {
    if (!detail?.formType) {
      return notifyError(t("表单类型信息缺失无法执行审批"));
    }

    const strategy =
      MAKE_VARIABLE_FOR_PURCHASING_STAFF_APPROVE_STRATEGIES[detail.formType];

    if (!strategy) {
      return notifyError(t("未配置采购人员审核策略"));
    }

    let variable: Record<string, any>;
    try {
      variable = await strategy(form);
    } catch (error) {
      return notifyError(extractFormFieldError(error));
    }

    executeApprove({
      skipType: resolveSkipType(approveValues.skipType),
      message: approveValues.message,
      variable,
    });
  };

  // 采购经理审批 （Low Value)
  const purchasingManagerApproveForLow = async (
    approveValues: ApprovalFormFields,
  ) => {
    if (detail?.formType !== PurchaseRequestTypeMap.LowValuePurchaseRequisition)
      return;

    let variable: Record<string, any>;
    try {
      const values = await (
        form as FormInstance<LowValuePurchaseRequisitionFormValue>
      ).validateFields();

      if (!assertSupplierListCompleted(values.supplierList)) return;

      variable = {
        purchaseGroup: values.purchaseGroup,
        sourcingResultRemark: values.sourcingResultRemark,
        contractRequired: values.contractRequired,
        itemList: values.itemList?.map((item) => {
          return {
            ...item,
            deliveryDate: item.deliveryDate?.format("YYYY-MM-DD"),
            attachments: convertFileItemsToString(item.attachments),
          };
        }),
      };

      if (values.supplierList && values.supplierList.length > 0) {
        variable.supplierList = values.supplierList.map((item) => ({
          ...item,
          quotationFiles: convertFileItemsToString(item.quotationFiles),
        }));
      }
    } catch (error) {
      return notifyWarning(extractFormFieldError(error));
    }
    executeApprove({
      skipType: resolveSkipType(approveValues.skipType),
      message: approveValues.message,
      variable,
    });
  };

  // 申请人补充/更新文件 （High Value)
  const applicantAddOrUpdateFilesApproveForHigh = async (
    approveValues: ApprovalFormFields,
  ) => {
    if (
      detail?.formType !== PurchaseRequestTypeMap.HighValuePurchaseRequisition
    )
      return;

    let variable: Record<string, any>;
    try {
      const values = await (
        form as FormInstance<HighValuePurchaseRequisitionFormValue>
      ).validateFields();
      variable = {
        meetingMinutes: convertFileItemsToString(values.meetingMinutes),
        attachments: convertFileItemsToString(values.attachments),
        complianceAssessment: convertFileItemsToString(
          values.complianceAssessment,
        ),
      };
    } catch (error) {
      return notifyWarning(extractFormFieldError(error));
    }
    executeApprove({
      skipType: resolveSkipType(approveValues.skipType),
      message: approveValues.message,
      variable,
    });
  };

  // 采购补充/更新文件 （High Value)
  const purchaserAddOrUpdateFilesApproveForHigh = async (
    approveValues: ApprovalFormFields,
  ) => {
    if (
      detail?.formType !== PurchaseRequestTypeMap.HighValuePurchaseRequisition
    )
      return;

    let variable: Record<string, any> = {};

    try {
      const values = await (
        form as FormInstance<HighValuePurchaseRequisitionFormValue>
      ).validateFields();

      if (!assertSupplierListCompleted(values.supplierList)) return;

      variable = {
        vendorComparisonSummaryFiles: convertFileItemsToString(
          values.vendorComparisonSummaryFiles,
        ),
      };

      if (values.supplierList && values.supplierList.length > 0) {
        variable.supplierList = values.supplierList.map((item) => ({
          ...item,
          quotationFiles: convertFileItemsToString(item.quotationFiles),
        }));
      }
    } catch (error) {
      return notifyWarning(extractFormFieldError(error));
    }
    executeApprove({
      skipType: resolveSkipType(approveValues.skipType),
      message: approveValues.message,
      variable,
    });
  };

  // 财务人员初审 策略注册
  const MAKE_VARIABLE_FOR_FINANCIAL_FIRST_AUDIT_STRATEGIES: Record<
    string,
    ApprovalStrategy
  > = {
    [PurchaseRequestTypeMap.HighValuePurchaseRequisition]:
      makeVariableForHighValueFinancialFirstAudit,
    [PurchaseRequestTypeMap.LowValuePurchaseRequisition]:
      makeVariableForLowValueFinancialFirstAudit,
    [PurchaseRequestTypeMap.RdRawMaterialRequisition]:
      makeVariableForNormalFinancialFirstAudit,
    [PurchaseRequestTypeMap.RldReferenceProductRequisition]:
      makeVariableForNormalFinancialFirstAudit,
  };

  // 财务人员初审
  const financialFirstAuditApprove = async (
    approveValues: ApprovalFormFields,
  ) => {
    if (!detail?.formType) {
      return notifyError(t("表单类型信息缺失无法执行审批"));
    }

    const strategy =
      MAKE_VARIABLE_FOR_FINANCIAL_FIRST_AUDIT_STRATEGIES[detail.formType];

    if (!strategy) {
      return notifyError(t("未配置财务人员初审策略"));
    }

    let variable: Record<string, any>;
    try {
      variable = await strategy(form);
    } catch (error) {
      return notifyError(extractFormFieldError(error));
    }

    executeApprove({
      skipType: resolveSkipType(approveValues.skipType),
      message: approveValues.message,
      variable,
    });
  };

  // 上传合同
  const uploadContractSubmit = async (approveValues: ApprovalFormFields) => {
    if (
      detail?.formType !==
        PurchaseRequestTypeMap.HighValuePurchaseRequisition &&
      detail?.formType !== PurchaseRequestTypeMap.LowValuePurchaseRequisition
    )
      return;

    let variable: Record<string, any>;
    try {
      const values = await (
        form as FormInstance<
          | HighValuePurchaseRequisitionFormValue
          | LowValuePurchaseRequisitionFormValue
        >
      ).validateFields();

      variable = {
        contractFiles: convertFileItemsToString(values.contractFiles),
      };
    } catch (error) {
      notifyWarning(extractFormFieldError(error));
      return;
    }

    executeApprove({
      skipType: resolveSkipType(approveValues.skipType),
      message: approveValues.message,
      variable,
    });
  };

  // 财务创建固定资产
  const createFixedAssetsNoSubmit = async (
    approveValues: ApprovalFormFields,
  ) => {
    if (
      detail?.formType !==
        PurchaseRequestTypeMap.HighValuePurchaseRequisition &&
      detail?.formType !== PurchaseRequestTypeMap.LowValuePurchaseRequisition
    )
      return;
    let variable: Record<string, any>;
    try {
      const values = await (
        form as FormInstance<HighValuePurchaseRequisitionFormValue>
      ).validateFields();

      variable = {
        isFixedAssets: values.isFixedAssets,
        fixedAssetsNo: values.fixedAssetsNo,
      };
    } catch (error) {
      return notifyWarning(extractFormFieldError(error));
    }

    executeApprove({
      skipType: resolveSkipType(approveValues.skipType),
      message: approveValues.message,
      variable,
    });
  };

  function assertSupplierListCompleted(
    supplierList?: { supplierName: string }[] | null,
  ) {
    if (
      supplierList === null ||
      !supplierList?.length ||
      supplierList.some((item) => !item.supplierName?.trim())
    ) {
      notifyWarning(t("请完善寻源结果"));
      return false;
    }
    return true;
  }

  return {
    saveDraft,
    submit,
    approve,
    orderConfirm,
    purchaseManagerApproveForRldOrRd,
    purchasingStaffApproveForHighOrLow,
    purchasingManagerApproveForLow,
    applicantAddOrUpdateFilesApproveForHigh,
    purchaserAddOrUpdateFilesApproveForHigh,
    financialFirstAuditApprove,
    uploadContractSubmit,
    createFixedAssetsNoSubmit,
  };
}
